import React from 'react';
import { Link } from 'react-router-dom';
import HeroSlideshow from '../components/HeroSlideshow';
import projects from '../data/projectsData';
import usePageMeta from '../hooks/usePageMeta';
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_LINK } from '../config';

const heroImages = projects.slice(0, 6).map(project => ({ src: project.mainImage, alt: project.title }));

const services = [
  { icon: 'fa-home', title: 'בתים פרטיים', text: 'בנייה בהתאמה אישית של בתים בני 2–3 קומות, משלב התכנון ועד המסירה.' },
  { icon: 'fa-city', title: 'מבני מגורים', text: 'ביצוע שלד ועבודות גמר בפרויקטי מגורים — וילות, דו-משפחתיים ובנייני בוטיק.' },
  { icon: 'fa-industry', title: 'תעשיה ומסחר', text: 'הקמת מפעלים, מרכזים מסחריים וחדרי טרפו בשיטות בנייה מתקדמות.' },
  { icon: 'fa-school', title: 'מבני ציבור', text: 'בתי כנסת, ישיבות, גני ילדים ומקוואות — ברמת גימור ועיצוב גבוהה.' },
  { icon: 'fa-shield-alt', title: 'ממ"דים ומרחבים מוגנים', text: 'תוספות ממ"ד למבנים קיימים — מהר, נקי ולפי התקנים המחמירים ביותר.' },
  { icon: 'fa-hard-hat', title: 'ניהול ופיקוח', text: 'ניהול, תיאום ופיקוח מקצועי על פרויקטים מורכבים, עד הוצאת טופס 4.' },
];

const whyUs = [
  { icon: 'fa-user-check', title: 'ליווי אישי', text: 'מלווים אתכם צמוד לאורך כל הדרך — משלב הרעיון ועד מסירת המפתח.' },
  { icon: 'fa-clock', title: 'עמידה בלוחות זמנים', text: 'מתחייבים ללו"ז ועומדים בו, בלי להתפשר על איכות העבודה.' },
  { icon: 'fa-check-circle', title: 'איכות ללא פשרות', text: 'סטנדרט ביצוע גבוה, חומרים איכותיים והקפדה על כל פרט.' },
  { icon: 'fa-handshake', title: 'מחיר הוגן ושקוף', text: 'הצעת מחיר מפורטת והוגנת, בלי הפתעות בהמשך הדרך.' },
];

function HomePage() {
  usePageMeta(null, 'דביר הנדסה ובניה — קבלן בניין לבתים פרטיים, מבני ציבור, תעשיה וממ"דים. ניהול, פיקוח וביצוע פרויקטים עם ליווי אישי. התקשרו: 053-2284404');

  const featuredProjects = projects.slice(0, 3);

  return (
    <div id="main">
      {/* Hero */}
      <section className="hero-section">
        <HeroSlideshow images={heroImages}>
          <h1>דביר הנדסה ובניה</h1>
          <p>ניהול, פיקוח וביצוע פרויקטי בנייה — באיכות בלתי מתפשרת</p>
          <div className="hero-buttons">
            <a href={`tel:${PHONE_TEL}`} className="button primary large">
              <i className="fa fa-phone icon"></i> {PHONE_DISPLAY}
            </a>
            <a href={WHATSAPP_LINK} className="button whatsapp large" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-whatsapp icon"></i> דברו איתנו בוואטסאפ
            </a>
          </div>
        </HeroSlideshow>
      </section>

      {/* About */}
      <section className="post home-about">
        <h2>מי אנחנו</h2>
        <p>
          חברת "דביר הנדסה ובניה" הוקמה מתוך תשוקה לעולם הבנייה ואהבה ליצירה.
          אנו מתמחים בניהול, תיאום וביצוע פרויקטים מורכבים במגזר הפרטי, העסקי והציבורי.
          עם ניסיון של שנים, צוות מקצועי ומיומן ומחויבות בלתי מתפשרת לאיכות,
          אנו מבטיחים ליווי אישי ופתרונות הנדסיים מתקדמים לכל פרויקט — קטן כגדול.
        </p>
      </section>

      {/* Services */}
      <section className="home-section services-section">
        <div className="section-inner">
          <h2>תחומי ההתמחות שלנו</h2>
          <div className="services-grid">
            {services.map(service => (
              <div className="service-card" key={service.title}>
                <i className={`fa ${service.icon} service-icon`}></i>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured projects */}
      <section className="home-section featured-section">
        <div className="section-inner">
          <h2>פרויקטים נבחרים</h2>
          <div className="featured-grid">
            {featuredProjects.map(project => (
              <Link to={`/project/${project.slug}`} className="featured-card" key={project.id}>
                <img src={project.mainImage} alt={project.title} loading="lazy" decoding="async" />
                <div className="featured-card-body">
                  <h3>{project.title}</h3>
                  <p>{project.shortDescription}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="section-actions">
            <Link to="/projects" className="button">לכל הפרויקטים <i className="fa fa-arrow-left icon"></i></Link>
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="home-section why-section">
        <div className="section-inner">
          <h2>למה לבחור בנו?</h2>
          <div className="why-grid">
            {whyUs.map(item => (
              <div className="why-item" key={item.title}>
                <i className={`fa ${item.icon} why-icon`}></i>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="cta-banner">
        <div className="section-inner">
          <h2>יש לכם פרויקט? בואו נדבר</h2>
          <p>פגישת היכרות וייעוץ ראשוני — ללא עלות וללא התחייבות</p>
          <div className="hero-buttons">
            <a href={`tel:${PHONE_TEL}`} className="button primary large">
              <i className="fa fa-phone icon"></i> התקשרו עכשיו
            </a>
            <a href={WHATSAPP_LINK} className="button whatsapp large" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-whatsapp icon"></i> וואטסאפ
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
